/**
 * Packaging OS — Public Landing.
 *
 * Implements landing-login-wireframe-spec.md §1.
 * No marketing claims, no fake metrics, no Lorem ipsum.
 */

import { useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { CapabilityCard } from "../components/CapabilityCard";
import { Footer } from "../components/Footer";
import { PublicHeader } from "../components/PublicHeader";
import { TrustBanner } from "../components/TrustBanner";
import { strings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { useLocaleSwitch } from "../i18n/useLocaleSwitch";

export function PublicLanding(): JSX.Element {
  const [locale] = useLocale();
  const setLocale = useLocaleSwitch();
  const t = strings(locale);
  const navigate = useNavigate();

  return (
    <div className="po-page">
      <PublicHeader
        locale={locale}
        onLocaleChange={setLocale}
        switchLanguageLabel={t.switchLanguageLabel}
        brand={t.appName}
        navLinks={[
          { to: "/login", label: t.ctaSignInWithGoogle.split(" ")[0] /* short-form for nav */ },
          { to: "/preview", label: t.ctaRequestAccess },
        ]}
      />

      <main className="po-page-main po-page-main--landing">
        <TrustBanner message={t.trustBannerFull} />

        <section className="po-hero" aria-labelledby="po-hero-title">
          <h1 id="po-hero-title" className="po-hero__title">
            {t.landingHeroTitle}
          </h1>
          <p className="po-hero__subtitle">{t.landingHeroSubtitle}</p>
          <div className="po-hero__actions">
            <Button variant="primary" onClick={() => navigate("/login")}>
              {t.ctaSignInWithGoogle}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/preview")}>
              {t.ctaRequestAccess}
            </Button>
          </div>
        </section>

        <section className="po-trust-strip" aria-labelledby="po-trust-title">
          <h2 id="po-trust-title" className="po-trust-strip__title">
            {t.landingTrustStripTitle}
          </h2>
          <ul className="po-trust-strip__list">
            <li>{t.landingTrustStripDraftItem}</li>
            <li>{t.landingTrustStripNoLiveAiItem}</li>
            <li>{t.landingTrustStripControlledAccessItem}</li>
          </ul>
        </section>

        <section aria-labelledby="po-cap-title">
          <header className="po-section-header">
            <span className="po-section-header__eyebrow">
              {t.landingCapabilityHeading}
            </span>
            <h2
              id="po-cap-title"
              className="po-section-header__title"
            >
              {t.landingCapabilityHeading}
            </h2>
          </header>
          <div className="po-grid po-grid--3">
            <CapabilityCard
              title={t.landingCapabilityCatalogTitle}
              body={t.landingCapabilityCatalogBody}
            />
            <CapabilityCard
              title={t.landingCapabilityWorkflowsTitle}
              body={t.landingCapabilityWorkflowsBody}
            />
            <CapabilityCard
              title={t.landingCapabilityTrustTitle}
              body={t.landingCapabilityTrustBody}
            />
          </div>
        </section>

        <section aria-labelledby="po-flow-title" className="po-stack">
          <header className="po-section-header">
            <h2
              id="po-flow-title"
              className="po-section-header__title"
            >
              {t.landingWorkflowPreviewHeading}
            </h2>
          </header>
          <p className="po-note">{t.landingWorkflowPreviewBody}</p>
        </section>

        <section aria-labelledby="po-access-title" className="po-stack">
          <header className="po-section-header">
            <h2
              id="po-access-title"
              className="po-section-header__title"
            >
              {t.landingAccessHeading}
            </h2>
          </header>
          <p className="po-note">{t.landingAccessBody}</p>
        </section>

        <section
          aria-labelledby="po-cta-title"
          className="po-stack"
          style={{ alignItems: "flex-start" }}
        >
          <header className="po-section-header">
            <h2
              id="po-cta-title"
              className="po-section-header__title"
            >
              {t.landingCtaSectionHeading}
            </h2>
          </header>
          <p className="po-note">{t.landingCtaSectionBody}</p>
          <div className="po-row">
            <Button variant="primary" onClick={() => navigate("/login")}>
              {t.ctaSignInWithGoogle}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/preview")}>
              {t.ctaRequestAccess}
            </Button>
          </div>
        </section>

        <p className="po-note po-note--muted po-note--small">
          {t.landingFooterEcosystem}
        </p>
      </main>

      <Footer copyright={t.copyrightLine} />
    </div>
  );
}
